// @src/pre-deployment/src/utils/validationSchemas.js v1.0
// - Node.js compatible runtime validation schemas
// - Throws ValidationError with detailed field paths
// - Export for Firebase Functions use
// - No dependencies on React or browser APIs

// ============================================================================
// VALIDATION ERROR CLASS
// ============================================================================

/**
 * Custom error class for validation failures
 * Contains detailed information about what field failed and why
 */
class ValidationError extends Error {
  constructor(message, errors = [], context = {}) {
    super(message);
    this.name = 'ValidationError';
    this.errors = errors; // Array of { path, expected, got, message }
    this.context = context; // { level, chapter, moduleKey, firestorePath, etc }
    this.timestamp = new Date().toISOString();
    this.isValidationError = true; // Easy type check
  }

  /**
   * Get a formatted error message with all field paths
   */
  getDetailedMessage() {
    if (this.errors.length === 0) return this.message;

    const fieldMessages = this.errors.map(err =>
      `  - ${err.path}: expected ${err.expected}, got ${typeof err.got === 'object' ? JSON.stringify(err.got) : err.got}${err.message ? ` (${err.message})` : ''}`
    ).join('\n');

    return `${this.message}\nValidation errors:\n${fieldMessages}`;
  }

  /**
   * Get error summary for logging
   */
  toLogObject() {
    return {
      name: this.name,
      message: this.message,
      errors: this.errors,
      context: this.context,
      timestamp: this.timestamp,
      errorCount: this.errors.length,
      fieldPaths: this.errors.map(e => e.path)
    };
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a value exists and is not null/undefined
 */
const exists = (value) => value !== null && value !== undefined;

/**
 * Check if a value is a non-empty string
 */
const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

/**
 * Check if a value is a positive integer
 */
const isPositiveInteger = (value) => typeof value === 'number' && Number.isInteger(value) && value > 0;

/**
 * Check if a value is a non-negative integer
 */
const isNonNegativeInteger = (value) => typeof value === 'number' && Number.isInteger(value) && value >= 0;

/**
 * Check if a value is an array
 */
const isArray = (value) => Array.isArray(value);

/**
 * Check if a value is a plain object
 */
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Add an error to the errors array
 */
const addError = (errors, path, expected, got, message = '') => {
  errors.push({ path, expected, got, message });
};

// ============================================================================
// DIALOGUE DATA VALIDATION
// ============================================================================

/**
 * Validate dialogue data structure
 * @param {Object} rawData - Raw dialogue data from Firebase
 * @param {Object} context - Context for error reporting { level, chapter, dialogueType, firestorePath }
 * @returns {Object} Validated data (unchanged if valid)
 * @throws {ValidationError} If validation fails
 */
const validateDialogueData = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'dialogue';

  // Check raw data exists
  if (!exists(rawData)) {
    addError(errors, path, 'object', rawData, 'Dialogue data is null or undefined');
    throw new ValidationError(`Dialogue validation failed: Data is missing`, errors, context);
  }

  if (!isObject(rawData)) {
    addError(errors, path, 'object', typeof rawData, 'Dialogue data must be an object');
    throw new ValidationError(`Dialogue validation failed: Invalid data type`, errors, context);
  }

  // Check required fields
  if (!exists(rawData.steps) || !isObject(rawData.steps)) {
    addError(errors, `${path}.steps`, 'object', rawData.steps, 'steps field is required and must be an object');
  }

  if (!exists(rawData.total_steps) || !isPositiveInteger(rawData.total_steps)) {
    addError(errors, `${path}.total_steps`, 'positive integer', rawData.total_steps, 'total_steps field is required');
  }

  // Validate each step if steps exist
  if (isObject(rawData.steps)) {
    Object.entries(rawData.steps).forEach(([stepKey, step]) => {
      const stepPath = `${path}.steps.${stepKey}`;

      if (!isObject(step)) {
        addError(errors, stepPath, 'object', typeof step, 'Each step must be an object');
        return;
      }

      // Required step fields
      if (!isNonEmptyString(step.speaker)) {
        addError(errors, `${stepPath}.speaker`, 'non-empty string', step.speaker, 'speaker field is required');
      }

      if (!isNonEmptyString(step.korean)) {
        addError(errors, `${stepPath}.korean`, 'non-empty string', step.korean, 'korean text is required');
      }

      // Optional but validated if present
      if (exists(step.english) && typeof step.english !== 'string') {
        addError(errors, `${stepPath}.english`, 'string', typeof step.english, 'english must be a string if present');
      }
    });
  }

  // Validate characters if present
  if (exists(rawData.characters)) {
    if (!isObject(rawData.characters)) {
      addError(errors, `${path}.characters`, 'object', typeof rawData.characters, 'characters must be an object');
    } else {
      Object.entries(rawData.characters).forEach(([charKey, char]) => {
        const charPath = `${path}.characters.${charKey}`;
        if (!isObject(char)) {
          addError(errors, charPath, 'object', typeof char, 'Each character must be an object');
        }
      });
    }
  }

  // Throw if any errors found
  if (errors.length > 0) {
    throw new ValidationError(
      `Dialogue validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

/**
 * Validate dialogue array structure
 * @param {Array} rawData - Raw dialogue array from Firebase
 * @param {Object} context - Context for error reporting
 * @returns {Array} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateDialogueArray = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'dialogue_list';

  if (!exists(rawData)) {
    addError(errors, path, 'array', rawData, 'Dialogue list is null or undefined');
    throw new ValidationError(`Dialogue validation failed: Data is missing`, errors, context);
  }

  if (!isArray(rawData)) {
    addError(errors, path, 'array', typeof rawData, 'Dialogue data must be an array');
    throw new ValidationError(`Dialogue validation failed: Invalid data type`, errors, context);
  }

  // Validate each dialogue document
  rawData.forEach((dialogue, index) => {
    try {
      validateDialogueData(dialogue, { ...context, firestorePath: `${path}[${index}]` });
    } catch (docError) {
      if (docError.isValidationError) {
        errors.push(...docError.errors);
      } else {
        addError(errors, `${path}[${index}]`, 'valid dialogue document', docError.message);
      }
    }
  });

  // Only throw if there are critical errors
  if (errors.length > 0) {
    throw new ValidationError(
      `Dialogue list validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

// ============================================================================
// COMIC DATA VALIDATION
// ============================================================================

/**
 * Validate comic page data structure
 * @param {Object} rawData - Raw comic data from Firebase Storage
 * @param {Object} context - Context for error reporting { comicId, pageId, storagePath }
 * @returns {Object} Validated data (unchanged if valid)
 * @throws {ValidationError} If validation fails
 */
const validateComicData = (rawData, context = {}) => {
  const errors = [];
  const path = context.storagePath || 'comic';

  // Check raw data exists
  if (!exists(rawData)) {
    addError(errors, path, 'object', rawData, 'Comic data is null or undefined');
    throw new ValidationError(`Comic validation failed: Data is missing`, errors, context);
  }

  if (!isObject(rawData)) {
    addError(errors, path, 'object', typeof rawData, 'Comic data must be an object');
    throw new ValidationError(`Comic validation failed: Invalid data type`, errors, context);
  }

  // Validate bubbles array (main content)
  if (exists(rawData.bubbles)) {
    if (!isArray(rawData.bubbles)) {
      addError(errors, `${path}.bubbles`, 'array', typeof rawData.bubbles, 'bubbles must be an array');
    } else {
      rawData.bubbles.forEach((bubble, index) => {
        const bubblePath = `${path}.bubbles[${index}]`;

        if (!isObject(bubble)) {
          addError(errors, bubblePath, 'object', typeof bubble, 'Each bubble must be an object');
          return;
        }

        // CRITICAL: speaker field is required for each bubble (this was causing the TypeError)
        if (!isNonEmptyString(bubble.speaker)) {
          addError(errors, `${bubblePath}.speaker`, 'non-empty string', bubble.speaker, 'speaker field is REQUIRED for each bubble');
        }

        // speaker_bbox is required for rendering
        if (!exists(bubble.speaker_bbox)) {
          addError(errors, `${bubblePath}.speaker_bbox`, 'array or object', bubble.speaker_bbox, 'speaker_bbox is required for rendering');
        }

        // text field validation
        if (exists(bubble.text) && typeof bubble.text !== 'string') {
          addError(errors, `${bubblePath}.text`, 'string', typeof bubble.text, 'text must be a string');
        }

        // bbox validation if present
        if (exists(bubble.bbox) && !isArray(bubble.bbox)) {
          addError(errors, `${bubblePath}.bbox`, 'array', typeof bubble.bbox, 'bbox must be an array');
        }
      });
    }
  }

  // Validate reading_sequence if present
  if (exists(rawData.reading_sequence)) {
    if (!isArray(rawData.reading_sequence)) {
      addError(errors, `${path}.reading_sequence`, 'array', typeof rawData.reading_sequence, 'reading_sequence must be an array');
    }
  }

  // Validate image dimensions if present
  if (exists(rawData.image_width) && !isPositiveInteger(rawData.image_width)) {
    addError(errors, `${path}.image_width`, 'positive integer', rawData.image_width);
  }

  if (exists(rawData.image_height) && !isPositiveInteger(rawData.image_height)) {
    addError(errors, `${path}.image_height`, 'positive integer', rawData.image_height);
  }

  // Throw if any errors found
  if (errors.length > 0) {
    throw new ValidationError(
      `Comic validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

// ============================================================================
// VOCABULARY DATA VALIDATION
// ============================================================================

/**
 * Validate vocabulary word structure
 * @param {Object} rawData - Raw vocabulary word data from Firebase
 * @param {Object} context - Context for error reporting
 * @returns {Object} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateVocabularyWord = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'vocabulary_word';

  if (!exists(rawData)) {
    addError(errors, path, 'object', rawData, 'Vocabulary word data is null or undefined');
    throw new ValidationError(`Vocabulary word validation failed: Data is missing`, errors, context);
  }

  if (!isObject(rawData)) {
    addError(errors, path, 'object', typeof rawData, 'Vocabulary word must be an object');
    throw new ValidationError(`Vocabulary word validation failed: Invalid data type`, errors, context);
  }

  // Required: korean text
  if (!isNonEmptyString(rawData.korean)) {
    addError(errors, `${path}.korean`, 'non-empty string', rawData.korean, 'korean field is required');
  }

  // Translations validation
  if (exists(rawData.translations)) {
    if (!isObject(rawData.translations)) {
      addError(errors, `${path}.translations`, 'object', typeof rawData.translations, 'translations must be an object');
    } else {
      // At least one translation should exist
      const hasTranslation = Object.values(rawData.translations).some(t => isNonEmptyString(t));
      if (!hasTranslation) {
        addError(errors, `${path}.translations`, 'at least one translation', rawData.translations, 'At least one translation is required');
      }
    }
  }

  if (errors.length > 0) {
    throw new ValidationError(
      `Vocabulary word validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

/**
 * Validate vocabulary array structure
 * @param {Array} rawData - Raw vocabulary array from Firebase
 * @param {Object} context - Context for error reporting
 * @returns {Array} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateVocabularyArray = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'vocabulary';

  if (!exists(rawData)) {
    addError(errors, path, 'array', rawData, 'Vocabulary data is null or undefined');
    throw new ValidationError(`Vocabulary validation failed: Data is missing`, errors, context);
  }

  if (!isArray(rawData)) {
    addError(errors, path, 'array', typeof rawData, 'Vocabulary data must be an array');
    throw new ValidationError(`Vocabulary validation failed: Invalid data type`, errors, context);
  }

  // Validate each word (but don't fail entire array for individual word issues)
  rawData.forEach((word, index) => {
    try {
      validateVocabularyWord(word, { ...context, firestorePath: `${path}[${index}]` });
    } catch (wordError) {
      if (wordError.isValidationError) {
        errors.push(...wordError.errors);
      }
    }
  });

  // Only throw if there are critical errors (empty array is allowed)
  if (errors.length > 0 && rawData.length === 0) {
    throw new ValidationError(
      `Vocabulary validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

// ============================================================================
// GRAMMAR DATA VALIDATION
// ============================================================================

/**
 * Validate grammar point structure
 * @param {Object} rawData - Raw grammar point data from Firebase
 * @param {Object} context - Context for error reporting
 * @returns {Object} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateGrammarPoint = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'grammar_point';

  if (!exists(rawData)) {
    addError(errors, path, 'object', rawData, 'Grammar point data is null or undefined');
    throw new ValidationError(`Grammar point validation failed: Data is missing`, errors, context);
  }

  if (!isObject(rawData)) {
    addError(errors, path, 'object', typeof rawData, 'Grammar point must be an object');
    throw new ValidationError(`Grammar point validation failed: Invalid data type`, errors, context);
  }

  // Required: id field
  if (!isNonEmptyString(rawData.id)) {
    addError(errors, `${path}.id`, 'non-empty string', rawData.id, 'id field is required');
  }

  // Title fields (at least one required)
  const hasTitle = isNonEmptyString(rawData.title_kr) ||
    isNonEmptyString(rawData.titleKR) ||
    isNonEmptyString(rawData.grammarPoint);
  if (!hasTitle) {
    addError(errors, `${path}.title_kr`, 'non-empty string', rawData.title_kr, 'At least one title field (title_kr, titleKR, grammarPoint) is required');
  }

  // Explanation validation (can be string or object)
  if (exists(rawData.explanation)) {
    if (typeof rawData.explanation !== 'string' && !isObject(rawData.explanation)) {
      addError(errors, `${path}.explanation`, 'string or object', typeof rawData.explanation, 'explanation must be a string or object');
    }
  }

  // Examples validation
  const examples = rawData.regular_examples || rawData.examples || rawData.step1?.examples || [];
  if (exists(examples) && !isArray(examples)) {
    addError(errors, `${path}.regular_examples`, 'array', typeof examples, 'examples must be an array');
  }

  if (errors.length > 0) {
    throw new ValidationError(
      `Grammar point validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

/**
 * Validate grammar points result structure
 * @param {Object} rawData - Raw grammar points result from GrammarService
 * @param {Object} context - Context for error reporting
 * @returns {Object} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateGrammarPointsResult = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'grammar_points';

  if (!exists(rawData)) {
    addError(errors, path, 'object', rawData, 'Grammar points result is null or undefined');
    throw new ValidationError(`Grammar points validation failed: Data is missing`, errors, context);
  }

  if (!isObject(rawData)) {
    addError(errors, path, 'object', typeof rawData, 'Grammar points result must be an object');
    throw new ValidationError(`Grammar points validation failed: Invalid data type`, errors, context);
  }

  // Required: learning array
  if (!exists(rawData.learning) || !isArray(rawData.learning)) {
    addError(errors, `${path}.learning`, 'array', rawData.learning, 'learning array is required');
  } else {
    // Validate each grammar point
    rawData.learning.forEach((point, index) => {
      try {
        validateGrammarPoint(point, { ...context, firestorePath: `${path}.learning[${index}]` });
      } catch (pointError) {
        if (pointError.isValidationError) {
          errors.push(...pointError.errors);
        }
      }
    });
  }

  // Source field (informational)
  if (!isNonEmptyString(rawData.source)) {
    // Warning only, not an error
    console.warn(`Grammar points missing source field at ${path}`);
  }

  if (errors.length > 0) {
    throw new ValidationError(
      `Grammar points validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

// ============================================================================
// EXERCISE DATA VALIDATION
// ============================================================================

/**
 * Validate exercise structure
 * @param {Object} rawData - Raw exercise data
 * @param {Object} context - Context for error reporting
 * @returns {Object} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateExercise = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'exercise';

  if (!exists(rawData)) {
    addError(errors, path, 'object', rawData, 'Exercise data is null or undefined');
    throw new ValidationError(`Exercise validation failed: Data is missing`, errors, context);
  }

  if (!isObject(rawData)) {
    addError(errors, path, 'object', typeof rawData, 'Exercise must be an object');
    throw new ValidationError(`Exercise validation failed: Invalid data type`, errors, context);
  }

  // Required: id
  if (!isNonEmptyString(rawData.id)) {
    addError(errors, `${path}.id`, 'non-empty string', rawData.id, 'id is required');
  }

  // Required: type
  if (!isNonEmptyString(rawData.type)) {
    addError(errors, `${path}.type`, 'non-empty string', rawData.type, 'type is required');
  }

  // Required: question or instruction
  if (!isNonEmptyString(rawData.question) && !isNonEmptyString(rawData.instruction)) {
    addError(errors, `${path}.question`, 'non-empty string', rawData.question, 'question or instruction is required');
  }

  // Required: answer
  if (!exists(rawData.answer) && !exists(rawData.accepted_answers)) {
    addError(errors, `${path}.answer`, 'string or array', rawData.answer, 'answer or accepted_answers is required');
  }

  if (errors.length > 0) {
    throw new ValidationError(
      `Exercise validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

/**
 * Validate exercise array structure
 * @param {Array} rawData - Raw exercise array
 * @param {Object} context - Context for error reporting
 * @returns {Array} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateExerciseArray = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'exercises';

  if (!exists(rawData)) {
    addError(errors, path, 'array', rawData, 'Exercise data is null or undefined');
    throw new ValidationError(`Exercise array validation failed: Data is missing`, errors, context);
  }

  if (!isArray(rawData)) {
    addError(errors, path, 'array', typeof rawData, 'Exercise data must be an array');
    throw new ValidationError(`Exercise array validation failed: Invalid data type`, errors, context);
  }

  // Validate each exercise
  rawData.forEach((exercise, index) => {
    try {
      validateExercise(exercise, { ...context, firestorePath: `${path}[${index}]` });
    } catch (exerciseError) {
      if (exerciseError.isValidationError) {
        errors.push(...exerciseError.errors);
      }
    }
  });

  if (errors.length > 0) {
    throw new ValidationError(
      `Exercise array validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

// ============================================================================
// LEARNING MODULES ORDER VALIDATION
// ============================================================================

/**
 * Validate learning modules order structure
 * @param {Object} rawData - Raw modules order data from Firebase
 * @param {Object} context - Context for error reporting
 * @returns {Object} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateModulesOrder = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'modules_order';

  if (!exists(rawData)) {
    addError(errors, path, 'object', rawData, 'Modules order data is null or undefined');
    throw new ValidationError(`Modules order validation failed: Data is missing`, errors, context);
  }

  if (!isObject(rawData)) {
    addError(errors, path, 'object', typeof rawData, 'Modules order must be an object');
    throw new ValidationError(`Modules order validation failed: Invalid data type`, errors, context);
  }

  // modulesOrder array is required
  if (!exists(rawData.modulesOrder) || !isArray(rawData.modulesOrder)) {
    addError(errors, `${path}.modulesOrder`, 'array', rawData.modulesOrder, 'modulesOrder array is required');
  } else if (rawData.modulesOrder.length === 0) {
    addError(errors, `${path}.modulesOrder`, 'non-empty array', '[]', 'modulesOrder cannot be empty');
  }

  // moduleMap is required
  if (!exists(rawData.moduleMap) || !isObject(rawData.moduleMap)) {
    addError(errors, `${path}.moduleMap`, 'object', rawData.moduleMap, 'moduleMap object is required');
  }

  if (errors.length > 0) {
    throw new ValidationError(
      `Modules order validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

// ============================================================================
// USER PROGRESS VALIDATION
// ============================================================================

/**
 * Validate user progress structure
 * @param {Object} rawData - Raw user progress data from Firebase
 * @param {Object} context - Context for error reporting
 * @returns {Object} Validated data
 * @throws {ValidationError} If validation fails
 */
const validateUserProgress = (rawData, context = {}) => {
  const errors = [];
  const path = context.firestorePath || 'user_progress';

  if (!exists(rawData)) {
    // User progress can be null (new user) - return default structure
    return {
      level: context.level || 1,
      chapter: context.chapter || 1,
      modules: {},
      total_exercises: 0,
      completed_exercises: 0
    };
  }

  if (!isObject(rawData)) {
    addError(errors, path, 'object', typeof rawData, 'User progress must be an object');
    throw new ValidationError(`User progress validation failed: Invalid data type`, errors, context);
  }

  // Validate numeric fields if present
  if (exists(rawData.total_exercises) && !isNonNegativeInteger(rawData.total_exercises)) {
    addError(errors, `${path}.total_exercises`, 'non-negative integer', rawData.total_exercises);
  }

  if (exists(rawData.completed_exercises) && !isNonNegativeInteger(rawData.completed_exercises)) {
    addError(errors, `${path}.completed_exercises`, 'non-negative integer', rawData.completed_exercises);
  }

  if (errors.length > 0) {
    throw new ValidationError(
      `User progress validation failed: ${errors.length} field(s) invalid`,
      errors,
      context
    );
  }

  return rawData;
};

// ============================================================================
// GENERIC DATA VALIDATION
// ============================================================================

/**
 * Validate that data exists and is not null
 * @param {any} rawData - Any data
 * @param {Object} context - Context for error reporting
 * @returns {any} The data if valid
 * @throws {ValidationError} If data is null/undefined
 */
const validateExists = (rawData, context = {}) => {
  if (!exists(rawData)) {
    const errors = [{
      path: context.firestorePath || 'data',
      expected: 'non-null value',
      got: rawData,
      message: 'Data is null or undefined'
    }];
    throw new ValidationError(
      `Validation failed: Data is missing at ${context.firestorePath || 'unknown path'}`,
      errors,
      context
    );
  }
  return rawData;
};

/**
 * Validate data is an object
 * @param {any} rawData - Any data
 * @param {Object} context - Context for error reporting
 * @returns {Object} The data if valid
 * @throws {ValidationError} If data is not an object
 */
const validateObject = (rawData, context = {}) => {
  validateExists(rawData, context);

  if (!isObject(rawData)) {
    const errors = [{
      path: context.firestorePath || 'data',
      expected: 'object',
      got: typeof rawData,
      message: 'Data must be an object'
    }];
    throw new ValidationError(
      `Validation failed: Expected object at ${context.firestorePath || 'unknown path'}`,
      errors,
      context
    );
  }
  return rawData;
};

/**
 * Validate data is an array
 * @param {any} rawData - Any data
 * @param {Object} context - Context for error reporting
 * @returns {Array} The data if valid
 * @throws {ValidationError} If data is not an array
 */
const validateArray = (rawData, context = {}) => {
  validateExists(rawData, context);

  if (!isArray(rawData)) {
    const errors = [{
      path: context.firestorePath || 'data',
      expected: 'array',
      got: typeof rawData,
      message: 'Data must be an array'
    }];
    throw new ValidationError(
      `Validation failed: Expected array at ${context.firestorePath || 'unknown path'}`,
      errors,
      context
    );
  }
  return rawData;
};

// ============================================================================
// EXPORTS
// ============================================================================

// Export for both ES modules and CommonJS (Node.js Firebase Functions)
const exports_obj = {
  // Error class
  ValidationError,

  // Dialogue validation
  validateDialogueData,
  validateDialogueArray,

  // Comic validation
  validateComicData,

  // Vocabulary validation
  validateVocabularyWord,
  validateVocabularyArray,

  // Grammar validation
  validateGrammarPoint,
  validateGrammarPointsResult,

  // Exercise validation
  validateExercise,
  validateExerciseArray,

  // Modules order validation
  validateModulesOrder,

  // User progress validation
  validateUserProgress,

  // Generic validation
  validateExists,
  validateObject,
  validateArray,

  // Helper functions (for testing)
  helpers: {
    exists,
    isNonEmptyString,
    isPositiveInteger,
    isNonNegativeInteger,
    isArray,
    isObject,
    addError
  }
};

// ES Module export
export {
  ValidationError,
  validateDialogueData,
  validateDialogueArray,
  validateComicData,
  validateVocabularyWord,
  validateVocabularyArray,
  validateGrammarPoint,
  validateGrammarPointsResult,
  validateExercise,
  validateExerciseArray,
  validateModulesOrder,
  validateUserProgress,
  validateExists,
  validateObject,
  validateArray
};

export default exports_obj;

// CommonJS export for Node.js (Firebase Functions)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = exports_obj;
}
